import { Button } from '@/app/common/components/button/Button'
import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { TrashCanIcon } from '@/app/common/icons/general'
import { parseRequestError } from '@/app/common/utilities'
import { cn } from '@/lib/utils'
import { useDisableProgramCatalogCategory } from '@/queries/program_catalog/hooks/useDisableProgramCatalogCategory'
import { ProgramCategory } from '@/queries/program_catalog/schema'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import React from 'react'
import { useSelector } from 'react-redux'

type Props = {
  category: ProgramCategory
  onRemoveSuccess: () => void
}

export default function DisableCategoryPopup(props: Props) {
  const { category, onRemoveSuccess } = props
  const [isOpen, setIsOpen] = React.useState(false)
  const { toast } = useToast()
  const disableCategoryMutation = useDisableProgramCatalogCategory({
    onSuccess: () => {
      onRemoveSuccess()
      setIsOpen(false)
      toast({
        title: 'Category removed from program',
        description: `${category.global_category_name} has been removed from the program.`,
        variant: 'default',
      })
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error removing category',
        description: parseRequestError(error).message,
        variant: 'destructive',
      })
    },
  })
  const programId = useSelector(selectSelectedProgram).id

  const isDisabled = category.status === 'Inactive'
  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger className="flex items-center gap-2" asChild>
        <button
          disabled={isDisabled}
          className={cn(
            'w-full hover:bg-gray-100 text-left transition rounded px-2 py-2',
            { 'cursor-not-allowed opacity-50': isDisabled }
          )}
        >
          <TrashCanIcon /> Remove from program
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Remove Category from Program</DialogTitle>
        <p className="text-sm text-gray-700">
          Are you sure you want to remove {category.global_category_name} from
          this program? Doing so will disable all items associated with this
          category for all applicants and providers within this program.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <div>
            <SubmitButton
              className={buttonVariants({
                variant: 'destructive',
              })}
              onClick={() => {
                disableCategoryMutation.mutate({
                  programId: programId,
                  categoryId: category.id,
                })
              }}
              submittingText="Removing..."
              buttonTxt="Remove"
              status={disableCategoryMutation.status}
            />
          </div>
          <Button variant={'outline'} onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
